<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, invoiceStatusClass, invoiceStatusLabel } from '../utils/format'
import type { AllocationHistoryRow, Invoice } from '../types/finance'

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()
const { getInvoice, listAllocationHistory } = useFinanceApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canPayments = computed(() => permissions.value.includes('payments.view'))
const canCreatePayment = computed(() => permissions.value.includes('payments.create'))

const invoiceId = computed(() => String(route.params.id ?? ''))
const invoice = ref<Invoice | null>(null)
const allocations = ref<AllocationHistoryRow[]>([])
const loading = ref(true)
const error = ref('')

const allocationsError = ref('')
const activeTab = ref<'lines' | 'allocations'>('lines')

const paidAmount = computed(() =>
  allocations.value
    .filter((allocation) => allocation.allocation_status === 'active')
    .reduce((sum, allocation) => sum + Number(allocation.allocated_amount ?? 0), 0),
)

const outstandingAmount = computed(() =>
  Number(invoice.value?.total_amount ?? 0) - paidAmount.value,
)

async function load() {
  loading.value = true
  error.value = ''
  allocationsError.value = ''
  try {
    invoice.value = await getInvoice(invoiceId.value)
  } catch (cause) {
    error.value = formatApiError(cause)
    loading.value = false
    return
  }
  loading.value = false

  if (!canPayments.value) return
  try {
    allocations.value = await listAllocationHistory('customer', invoiceId.value)
  } catch (cause) {
    allocationsError.value = formatApiError(cause)
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/receivables/invoices">← {{ t('finance.receivables.title') }}</RouterLink>
    <h2>{{ t('finance.receivables.detailTitle', { id: invoiceId }) }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else-if="invoice">
    <div class="toolbar">
      <div class="button-group">
        <RouterLink
          v-if="canCreatePayment"
          class="button-group__item is-primary"
          :to="`/receivables/collections/new?agent_id=${invoice.agent_id}&invoice_id=${invoice.id}&amount=${outstandingAmount}`"
        >
          {{ t('finance.payments.recordForInvoice') }}
        </RouterLink>
        <RouterLink class="button-group__item" to="/receivables/collections">
          {{ t('finance.nav.collections') }}
        </RouterLink>
        <button class="button-group__item" :disabled="loading" @click="load">
          {{ t('finance.common.refresh') }}
        </button>
      </div>
      <span class="spacer" />
      <div class="summary">
        <span class="status-badge" :class="invoiceStatusClass(invoice.status)">
          {{ invoiceStatusLabel(t, invoice.status) }}
        </span>
        <span>{{ invoice.agent_name || '—' }}</span>
        <span>{{ invoice.currency_code }}</span>
      </div>
    </div>

    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.receivables.amount') }}</span>
        <span class="summary-value">
          {{ formatMoney(invoice.total_amount, invoice.currency_code) }}
        </span>
        <span class="summary-label">{{ formatDisplayDate(invoice.created_at) }}</span>
      </div>
      <div class="summary-card accent">
        <span class="summary-label">{{ t('finance.receivables.paid') }}</span>
        <span class="summary-value">{{ formatMoney(paidAmount, invoice.currency_code) }}</span>
        <span class="summary-label">{{ t('finance.common.exchangeRate') }}: {{ invoice.exchange_rate }}</span>
      </div>
      <div class="summary-card" :class="{ attention: outstandingAmount > 0 }">
        <span class="summary-label">{{ t('finance.receivables.outstanding') }}</span>
        <span class="summary-value">{{ formatMoney(outstandingAmount, invoice.currency_code) }}</span>
        <span class="summary-label">
          {{ t('finance.receivables.order') }}: {{ invoice.order_id ?? '—' }}
        </span>
      </div>
    </div>

    <div class="tabs">
      <button
        class="tab-button"
        :class="{ 'is-active': activeTab === 'lines' }"
        @click="activeTab = 'lines'"
      >
        {{ t('finance.receivables.lines') }}
      </button>
      <button
        class="tab-button"
        :class="{ 'is-active': activeTab === 'allocations' }"
        @click="activeTab = 'allocations'"
      >
        {{ t('finance.receivables.allocations') }}
      </button>
    </div>

    <div class="tab-block">
      <div v-if="activeTab === 'lines'">
        <div v-if="invoice.lines?.length" class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>{{ t('finance.receivables.product') }}</th>
                <th>{{ t('finance.receivables.sku') }}</th>
                <th class="cell-num">{{ t('finance.receivables.quantity') }}</th>
                <th class="cell-num">{{ t('finance.receivables.unitPrice') }}</th>
                <th class="cell-num">{{ t('finance.receivables.lineTotal') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in invoice.lines" :key="line.id">
                <td class="cell-name">{{ line.product_name || '—' }}</td>
                <td class="cell-code">{{ line.sku || '—' }}</td>
                <td class="cell-num">{{ line.quantity }}</td>
                <td class="cell-num">{{ formatMoney(line.unit_price, invoice.currency_code) }}</td>
                <td class="cell-num">{{ formatMoney(line.line_total, invoice.currency_code) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-inline">{{ t('finance.common.empty') }}</p>
      </div>

      <div v-else class="panel">
        <p v-if="allocationsError" class="error-inline">{{ allocationsError }}</p>
        <div v-else-if="allocations.length" class="table-scroll">
          <table class="data-table compact">
            <thead>
              <tr>
                <th>{{ t('finance.receivables.allocationDate') }}</th>
                <th>{{ t('finance.common.payments') }}</th>
                <th>{{ t('finance.common.reference') }}</th>
                <th class="cell-num">{{ t('finance.receivables.allocatedAmount') }}</th>
                <th>{{ t('finance.common.status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="allocation in allocations" :key="allocation.id">
                <td class="cell-date">{{ formatDisplayDate(allocation.allocation_date) }}</td>
                <td class="cell-code">#{{ allocation.payment_id }}</td>
                <td>{{ allocation.payment_reference || '—' }}</td>
                <td class="cell-num">
                  {{ formatMoney(allocation.allocated_amount, allocation.currency_code) }}
                </td>
                <td>
                  <span
                    class="status-badge"
                    :class="allocation.allocation_status === 'active' ? 'status-ok' : 'status-critical'"
                  >
                    {{ allocation.allocation_status === 'active'
                      ? t('finance.common.active')
                      : t('finance.adjustmentStatus.reversed') }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-inline">
          {{ canPayments ? t('finance.receivables.noAllocations') : t('finance.common.noPermission') }}
        </p>
      </div>
    </div>
  </template>
</template>
